import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

import config from "../config";

function getStatusColor(status) {
  switch (status) {
    case "new":
      return "info";
    case "viewed":
      return "info";
    case "contacted":
      return "warning";
    case "closed":
      return "success";
    default:
      return "default";
  }
}

function getFullName(user) {
  if (!user) return "-";
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return fullName || "-";
}

export default function InterestDetailsModal({
  open,
  onClose,
  interest,
  token,
  onStatusUpdated,
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("new");
  const [saving, setSaving] = useState(false);
  const [autoUpdatingStatus, setAutoUpdatingStatus] = useState(false);
  const [err, setErr] = useState("");

  const [reportMode, setReportMode] = useState(false);
  const [reportBody, setReportBody] = useState("");
  const [reportBodyError, setReportBodyError] = useState("");
  const [reportSaving, setReportSaving] = useState(false);
  const [reportErr, setReportErr] = useState("");
  const [reportDone, setReportDone] = useState(false);

  useEffect(() => {
    if (open) {
      setErr("");
      setSaving(false);
      setStatus(interest?.status || "new");
      setReportMode(false);
      setReportBody("");
      setReportBodyError("");
      setReportSaving(false);
      setReportErr("");
      setReportDone(false);
    }
  }, [open, interest]);

  useEffect(() => {
    const markAsViewed = async () => {
      if (!open || !interest?._id || !token) return;
      if (interest?.status !== "new") return;

      try {
        setAutoUpdatingStatus(true);
        setErr("");

        const res = await axios.patch(
          `${config.BACKEND_BASE_URL}/api/admin/interests/${interest._id}/status`,
          { status: "viewed" },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const updated = res.data?.interest;
        if (updated) {
          setStatus(updated.status || "viewed");
          onStatusUpdated?.(updated);
        } else {
          setStatus("viewed");
        }
      } catch (error) {
        console.error("Error updating interest status to viewed:", error);
        setErr(
          error?.response?.data?.message ||
            error?.message ||
            t("modals.interest.updateFailed")
        );
      } finally {
        setAutoUpdatingStatus(false);
      }
    };

    markAsViewed();
  }, [open, interest?._id, interest?.status, token, onStatusUpdated]);

  const handleSave = async () => {
    if (!interest?._id) return;

    try {
      setSaving(true);
      setErr("");

      const res = await axios.patch(
        `${config.BACKEND_BASE_URL}/api/admin/interests/${interest._id}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const updated = res.data?.interest;
      if (updated) {
        onStatusUpdated?.(updated);
      }

      onClose?.();
    } catch (error) {
      console.error("Error updating interest status:", error);
      setErr(
        error?.response?.data?.message ||
          error?.message ||
          t("modals.interest.updateFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReport = async () => {
    if (!reportBody.trim()) {
      setReportBodyError(t("modals.interest.reportBodyError"));
      return;
    }
    try {
      setReportSaving(true);
      setReportErr("");
      await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/authentication/support-request`,
        { title: "Affiliate Report", body: reportBody.trim() },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setReportDone(true);
    } catch (error) {
      setReportErr(
        error?.response?.data?.error ||
        error?.message ||
        t("modals.interest.reportFailed")
      );
    } finally {
      setReportSaving(false);
    }
  };

  const isBusy = saving || autoUpdatingStatus;

  const currentDisplayedStatus =
    interest?.status === "new" && status === "new" ? "new" : status;

  const statusOptions = useMemo(() => {
    if (status === "contacted" || status === "closed") {
      return ["contacted", "closed"];
    }
    return ["viewed", "contacted", "closed"];
  }, [status]);

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a2e",
          color: "#fff",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <MailOutlineIcon sx={{ color: "#fff" }} />
          <Typography fontWeight={900} variant="h6">
            {reportMode ? t("modals.interest.reportViewTitle") : t("modals.interest.title")}
          </Typography>
        </Stack>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "rgba(255,255,255,0.6)" }}
          disabled={isBusy}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {reportMode ? (
          <Stack spacing={2} mt={2}>
            {reportDone ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {t("modals.interest.reportSuccess")}
              </Alert>
            ) : (
              <>
                <TextField
                  label={t("modals.interest.reportBodyLabel")}
                  value={reportBody}
                  onChange={(e) => { setReportBody(e.target.value); setReportBodyError(""); }}
                  multiline
                  minRows={5}
                  fullWidth
                  error={!!reportBodyError}
                  helperText={reportBodyError || t("modals.interest.reportBodyHint")}
                  disabled={reportSaving}
                  InputLabelProps={{ sx: { color: "rgba(255,255,255,0.7)" } }}
                  FormHelperTextProps={{ sx: { color: reportBodyError ? undefined : "rgba(255,255,255,0.55)" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                      "&.Mui-focused fieldset": { borderColor: "#fff" },
                    },
                  }}
                />
                {reportErr && (
                  <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                    {reportErr}
                  </Alert>
                )}
              </>
            )}
          </Stack>
        ) : (
        <Stack spacing={2.5} mt={2}>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", mb: 0.75 }}
            >
              {t("modals.interest.fieldTitle")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#fff", fontWeight: 600, fontSize: "1rem" }}
            >
              {interest?.title || "-"}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", mb: 0.75 }}
              >
                {t("modals.interest.fieldName")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#fff",
                  fontSize: "0.95rem",
                  wordBreak: "break-word",
                }}
              >
                {getFullName(interest?.userId)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", mb: 0.75 }}
              >
                {t("modals.interest.fieldEmail")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#fff",
                  fontSize: "0.95rem",
                  wordBreak: "break-word",
                }}
              >
                {interest?.userEmail || interest?.userId?.email || "-"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", mb: 0.75 }}
              >
                {t("modals.interest.fieldPhone")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#fff", fontSize: "0.95rem" }}>
                {interest?.telephone || "-"}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", mb: 0.75 }}
              >
                {t("modals.interest.fieldCurrentStatus")}
              </Typography>

              <Chip
                label={t(`status.${currentDisplayedStatus}`) || "-"}
                color={getStatusColor(currentDisplayedStatus)}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
          </Stack>

          <Box>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", mb: 0.75 }}
            >
              {t("modals.interest.fieldChangeStatus")}
            </Typography>

            <FormControl fullWidth size="small">
              <Select
                value={status === "new" ? "" : status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={isBusy}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return t("modals.interest.selectStatus");
                  return t(`status.${selected}`) || "-";
                }}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.04)",
                  borderRadius: 2,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.2)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.35)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.45)",
                  },
                  ".MuiSelect-icon": {
                    color: "#fff",
                  },
                  px: 1,
                  py: 0.5,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#23233a",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      "& .MuiMenuItem-root": {
                        fontSize: "0.95rem",
                      },
                      "& .MuiMenuItem-root.Mui-selected": {
                        bgcolor: "rgba(255,255,255,0.08)",
                      },
                      "& .MuiMenuItem-root:hover": {
                        bgcolor: "rgba(255,255,255,0.06)",
                      },
                    },
                  },
                }}
              >
                {statusOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`status.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}
            >
              {t("modals.interest.fieldMessage")}
            </Typography>

            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.16)",
                bgcolor: "rgba(255,255,255,0.04)",
                minHeight: 140,
                p: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#fff",
                  whiteSpace: "pre-line",
                  lineHeight: 1.7,
                  fontSize: "0.95rem",
                }}
              >
                {interest?.message || "-"}
              </Typography>
            </Box>
          </Box>

          {err && (
            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
              {err}
            </Alert>
          )}
        </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          gap: 1,
        }}
      >
        {reportMode ? (
          <>
            <Button
              variant="outlined"
              onClick={() => setReportMode(false)}
              disabled={reportSaving}
              sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", flex: 1 }}
            >
              {t("common.close")}
            </Button>
            {!reportDone && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleReport}
                disabled={reportSaving}
                sx={{ flex: 1 }}
              >
                {reportSaving ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <span>{t("common.saving")}</span>
                  </Stack>
                ) : (
                  t("modals.interest.reportSubmit")
                )}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isBusy}
              sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", flex: 1 }}
            >
              {t("common.close")}
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={() => setReportMode(true)}
              disabled={isBusy}
              sx={{ flex: 1 }}
            >
              {t("modals.interest.reportButton")}
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                isBusy ||
                !interest?._id ||
                status === interest?.status ||
                status === "new"
              }
              sx={{ flex: 1 }}
            >
              {saving ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} color="inherit" />
                  <span>{t("common.saving")}</span>
                </Stack>
              ) : (
                t("common.save")
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
